/*
 * petngo-product-card.js
 * ---------------------------------------------------------------------------
 * Vanilla, dependency-free behaviour for `snippets/petngo-product-card.liquid`.
 *
 *   1. Swatch → image swap. Clicking a swatch repaints the card image to the
 *      variant image, updates the price/compareAt readout, re-points the
 *      add-to-cart hidden input to that variant, and moves radio selection
 *      (full keyboard support: arrows / Home / End).
 *
 *   2. Add to cart. Submits the card form via fetch() to /cart/add.js, then
 *      opens + refreshes the native Minimog cart drawer by dispatching the
 *      same `cart:refresh` event the theme's own drawer listens for. The
 *      header cart-count bubble is updated through Minimog's `cart-update`
 *      pub/sub. Errors surface inline on the button (no silent failures).
 *
 * Idempotent: guarded on window.__pnCardInit so the file can be included by
 * every card render without double-binding.
 */
(function () {
  'use strict';

  if (window.__pnCardInit) return;
  window.__pnCardInit = true;

  var BOUND = 'data-pn-card-bound';
  var ADDED_FEEDBACK_MS = 1800;
  var ERROR_FEEDBACK_MS = 2600;

  // --- locale strings (mirrors petngo.card.*); JS can't read locales so we
  //     read them off the rendered DOM where possible and fall back here. ---
  var STRINGS = {
    add: 'Añadir al carrito',
    added: 'Agregado',
    adding: 'Agregando…',
    error: 'Reintentar',
  };

  /** Safely emit a Minimog pub/sub event if the bus is present. */
  function emitMinimog(eventName, payload) {
    try {
      if (
        window.MinimogEvents &&
        typeof window.MinimogEvents.emit === 'function' &&
        window.MinimogTheme &&
        window.MinimogTheme.pubSubEvents &&
        window.MinimogTheme.pubSubEvents[eventName]
      ) {
        window.MinimogEvents.emit(
          window.MinimogTheme.pubSubEvents[eventName],
          payload
        );
      }
    } catch (e) {
      /* non-fatal — drawer refresh still happens via cart:refresh */
    }
  }

  /**
   * Open + refresh the native Minimog cart drawer. The <m-cart-drawer>
   * element listens for `cart:refresh` and, on detail.open === true,
   * re-fetches `?section_id=cart-drawer` and slides itself open. This is the
   * theme's own public hook — we reuse it rather than inventing a drawer.
   * Falls back to navigating to /cart when the drawer isn't on the page.
   */
  function openCartDrawer(cartData) {
    emitMinimog('cartUpdate', { cart: cartData });

    var drawer = document.getElementById('MinimogCartDrawer');
    if (drawer) {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { detail: { open: true } })
      );
      // Belt-and-suspenders: some builds also key off product-ajax:added.
      document.dispatchEvent(
        new CustomEvent('product-ajax:added', { detail: { product: cartData } })
      );
      return true;
    }
    return false;
  }

  // -------------------------------------------------------------------------
  // Swatch → image swap
  // -------------------------------------------------------------------------

  function applyVariant(card, swatch) {
    // Image is targeted by class: Shopify's `image_tag` filter can't take a
    // hyphenated `data-*` named argument (Liquid parses the dashes as
    // subtraction), so we keep the DS class and select on it here.
    var image = card.querySelector('.pn-product-card__img--primary');
    var priceEl = card.querySelector('[data-pn-card-price]');
    var compareEl = card.querySelector('[data-pn-card-compare]');
    var input = card.querySelector('[data-pn-card-variant-input]');
    var atcBtn = card.querySelector('[data-pn-card-atc-button]');

    var imgUrl = swatch.getAttribute('data-variant-image');
    var price = swatch.getAttribute('data-variant-price');
    var compare = swatch.getAttribute('data-variant-compare');
    var variantId = swatch.getAttribute('data-variant-id');
    var available = swatch.getAttribute('data-variant-available') === 'true';

    if (image && imgUrl) {
      // Preserve srcset semantics by swapping both src and srcset off the
      // single resolved URL — the browser picks the cached candidate.
      image.src = imgUrl;
      if (image.hasAttribute('srcset')) image.removeAttribute('srcset');
    }
    if (priceEl && price) priceEl.textContent = price;
    if (compareEl) {
      if (compare) {
        compareEl.textContent = compare;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
    if (input && variantId) input.value = variantId;

    // When the chosen variant is sold out, disable ATC defensively.
    if (atcBtn) {
      if (available) {
        atcBtn.removeAttribute('disabled');
        atcBtn.removeAttribute('aria-disabled');
      } else {
        atcBtn.setAttribute('disabled', 'disabled');
        atcBtn.setAttribute('aria-disabled', 'true');
      }
    }
  }

  function selectSwatch(card, swatches, idx, focus) {
    for (var i = 0; i < swatches.length; i++) {
      var isSel = i === idx;
      swatches[i].classList.toggle('pn-product-card__swatch--active', isSel);
      swatches[i].setAttribute('aria-checked', isSel ? 'true' : 'false');
      swatches[i].setAttribute('tabindex', isSel ? '0' : '-1');
    }
    applyVariant(card, swatches[idx]);
    if (focus && swatches[idx]) swatches[idx].focus();
  }

  function bindSwatches(card) {
    var row = card.querySelector('[data-pn-card-swatches]');
    if (!row) return;
    var swatches = Array.prototype.slice.call(
      row.querySelectorAll('[data-pn-card-swatch]')
    );
    if (swatches.length === 0) return;

    swatches.forEach(function (sw, idx) {
      sw.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        selectSwatch(card, swatches, idx, false);
      });
      sw.addEventListener('keydown', function (e) {
        var next = idx;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          next = (idx + 1) % swatches.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          next = (idx - 1 + swatches.length) % swatches.length;
        } else if (e.key === 'Home') {
          next = 0;
        } else if (e.key === 'End') {
          next = swatches.length - 1;
        } else {
          return;
        }
        e.preventDefault();
        selectSwatch(card, swatches, next, true);
      });
    });
  }

  // -------------------------------------------------------------------------
  // Add to cart
  // -------------------------------------------------------------------------

  function setButtonLabel(card, text) {
    var label = card.querySelector('[data-pn-card-atc-label]');
    if (label) label.textContent = text;
  }

  function announce(card, text) {
    var live = card.querySelector('[data-pn-card-live]');
    if (live) live.textContent = text;
  }

  function bindForm(card) {
    var form = card.querySelector('[data-pn-card-form]');
    if (!form) return;
    var button = card.querySelector('[data-pn-card-atc-button]');
    var label = card.querySelector('[data-pn-card-atc-label]');
    var idleLabel = label ? label.textContent.trim() : STRINGS.add;
    var title = form.getAttribute('data-product-title') || '';
    var resetTimer = null;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (button && button.hasAttribute('disabled')) return;

      if (resetTimer) {
        clearTimeout(resetTimer);
        resetTimer = null;
      }
      if (button) button.setAttribute('disabled', 'disabled');
      setButtonLabel(card, STRINGS.adding);

      var formData = new FormData(form);

      fetch(window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, status: res.status, data: data };
          });
        })
        .then(function (result) {
          // Shopify returns { status, message, description } on error.
          if (!result.ok || (result.data && result.data.status)) {
            throw new Error(
              (result.data && (result.data.description || result.data.message)) ||
                'cart_add_failed'
            );
          }

          setButtonLabel(card, STRINGS.added + ' ✓');
          announce(card, title + ' — ' + STRINGS.added);

          var opened = openCartDrawer(result.data);
          if (!opened) {
            // No drawer on page → hard-navigate so the add isn't lost.
            window.location.href =
              window.Shopify && window.Shopify.routes
                ? window.Shopify.routes.root + 'cart'
                : '/cart';
            return;
          }

          resetTimer = setTimeout(function () {
            setButtonLabel(card, idleLabel);
            if (button) button.removeAttribute('disabled');
          }, ADDED_FEEDBACK_MS);
        })
        .catch(function (err) {
          if (window.console && console.error) {
            console.error('[petngo-product-card] add to cart failed:', err);
          }
          setButtonLabel(card, STRINGS.error);
          announce(card, STRINGS.error);
          resetTimer = setTimeout(function () {
            setButtonLabel(card, idleLabel);
            if (button) button.removeAttribute('disabled');
          }, ERROR_FEEDBACK_MS);
        });
    });

    // ---- "Comprar ahora": add the default/selected variant, then go straight
    //      to /checkout. type=button so it doesn't trip the form's add+drawer
    //      submit handler above; reuses the same /cart/add.js endpoint. ----
    var buyNowBtn = card.querySelector('[data-pn-card-buynow]');
    if (buyNowBtn) {
      var buyNowLabelEl = card.querySelector('[data-pn-card-buynow-label]');
      var buyNowIdle = buyNowLabelEl ? buyNowLabelEl.textContent : '';
      var buyNowTimer = null;
      buyNowBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (buyNowBtn.hasAttribute('disabled')) return;
        if (button && button.hasAttribute('disabled')) return;
        if (buyNowTimer) {
          clearTimeout(buyNowTimer);
          buyNowTimer = null;
        }
        buyNowBtn.setAttribute('disabled', 'disabled');
        if (buyNowLabelEl) buyNowLabelEl.textContent = STRINGS.adding;

        fetch(window.Shopify && window.Shopify.routes
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: new FormData(form),
        })
          .then(function (res) {
            return res.json().then(function (data) {
              return { ok: res.ok, data: data };
            });
          })
          .then(function (result) {
            if (!result.ok || (result.data && result.data.status)) {
              throw new Error(
                (result.data && (result.data.description || result.data.message)) ||
                  'cart_add_failed'
              );
            }
            announce(card, title + ' — ' + STRINGS.added);
            window.location.href =
              window.Shopify && window.Shopify.routes
                ? window.Shopify.routes.root + 'checkout'
                : '/checkout';
          })
          .catch(function (err) {
            if (window.console && console.error) {
              console.error('[petngo-product-card] buy now failed:', err);
            }
            if (buyNowLabelEl) buyNowLabelEl.textContent = STRINGS.error;
            announce(card, STRINGS.error);
            buyNowTimer = setTimeout(function () {
              if (buyNowLabelEl) buyNowLabelEl.textContent = buyNowIdle;
              buyNowBtn.removeAttribute('disabled');
            }, ERROR_FEEDBACK_MS);
          });
      });
    }
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  function initCard(card) {
    if (card.hasAttribute(BOUND)) return;
    card.setAttribute(BOUND, '');
    bindSwatches(card);
    bindForm(card);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var cards = scope.querySelectorAll('[data-pn-card]');
    for (var i = 0; i < cards.length; i++) initCard(cards[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Re-init cards injected later (Theme Editor section reloads, infinite
  // scroll, quick-view). Section events from OS 2.0 + Minimog.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });
  document.addEventListener('cart:updated', function () {
    initAll(document);
  });
})();
